import Modal from 'react-modal'
import { modalStyles } from 'pages/styles'
import { Box, Button, Flex, Heading } from 'theme-ui'
import { useAuth } from 'providers'
import { useCallback } from 'react'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useHistory } from 'react-router-dom'
import onboardingModal from './onboarding_modal.gif'
import { useUser } from 'hooks/userHooks'
import { TEACHER } from '../../constants'
const LAST_TIME_MODAL_SHOWN_KEY = 'lastOnboardingRequestTime'
const WAIT_TO_RESHOW_MODAL = 86400000 //24Hours * 60 mins/hour * 60 secs/min * 1000 ms/sec
const now = () => new Date().getTime()

export const OnboardingModal = () => {
  const { profile } = useAuth()
  const history = useHistory()
  const path = history?.location?.pathname
  const isCustomerSupportUser = profile?.groups?.includes('SupportGroup')
  const isTeacherInTraining = profile?.userType === TEACHER
  if (
    isCustomerSupportUser ||
    isTeacherInTraining ||
    !profile ||
    (path && path.includes('/help'))
  ) {
    return null
  } else {
    return <TeacherProfileOnboardingModal />
  }
}

const OnboardingModalTemplate = ({
  isOpen,
  heading,
  children,
}: {
  isOpen: boolean
  heading?: string
  children: JSX.Element
}) => {
  return (
    <Modal
      isOpen={isOpen}
      style={{
        ...modalStyles,
        overlay: {
          ...modalStyles.overlay,
          backgroundColor: 'white',
        },
      }}
      ariaHideApp={false}>
      <Flex
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          pt: 0,
          bg: ['white', 'grey'],
          width: '100vw',
          height: '100vh',
          overflow: 'auto',
          alignItems: 'center',
        }}>
        <Box
          sx={{
            position: 'relative',
            pt: [38, 67],
            pl: [30, 60],
            pr: [30, 60],
            pb: [30, 60],
            maxWidth: [375, 800],
            margin: 'auto',
            backgroundColor: 'white',
            borderRadius: [0, 15],
          }}>
          {heading && (
            <Box
              variant={`forms.section.header`}
              sx={{
                pt: 0,
                pb: 4,
                maxWidth: [335, 'unset'],
              }}>
              <Heading
                variant="forms.h1"
                sx={{ pb: 16, lineHeight: '22px', fontSize: ['22px', '32px'] }}>
                {heading}
              </Heading>
            </Box>
          )}
          {children}
        </Box>
      </Flex>
    </Modal>
  )
}

export const TeacherProfileOnboardingModal = () => {
  const { profile } = useAuth()
  const history = useHistory()

  const userId = profile?.id
  const [user] = useUser(userId)
  const userLoaded = !!user?.forename
  const contactEmail = user?.contact?.default?.email
  const [lastTimeModalShown, setLastOnboarded] = useLocalStorage(
    LAST_TIME_MODAL_SHOWN_KEY
  )

  const canShowModalAgain = useCallback(() => {
    const nextTimeModalCanBeShown =
      Number(lastTimeModalShown) + WAIT_TO_RESHOW_MODAL
    if (isNaN(nextTimeModalCanBeShown) || nextTimeModalCanBeShown < now()) {
      return true
    } else {
      return false
    }
  }, [lastTimeModalShown])

  const shouldShowModal = useCallback(() => {
    if (userLoaded && !contactEmail && canShowModalAgain()) {
      return true
    } else {
      return false
    }
  }, [userLoaded, contactEmail, canShowModalAgain])

  const handleClicked = () => {
    setLastOnboarded(String(now()))
    history.push('/profile?edit=true')
  }

  const handleMoreInfo = () => {
    setLastOnboarded(String(now()))
    history.push('/help/article/6233b5f8c1e53608cf9e9f76')
  }

  return (
    <OnboardingModalTemplate
      isOpen={shouldShowModal()}
      heading="Complete your Teacher profile">
      <>
        <Box
          variant="text.informational"
          sx={{ fontSize: 16, lineHeight: '22px', pb: 40, pr: 5 }}>
          The new teacher profile is where you can edit your contact details and
          teaching locations. You can find it in your Menu.
        </Box>
        <Box variant="text.informational" sx={{ width: ['100%'], pb: 50 }}>
          <img
            src={onboardingModal}
            width="100%"
            alt="Teaching Profile Location"
            style={{ border: '2px solid lightGray' }}
          />
        </Box>

        <Flex
          variant="forms.buttons.container"
          sx={{
            maxWidth: ['unset', 'unset'],
            width: 'auto',
          }}>
          <Button variant="forms.buttons.primary" onClick={handleClicked}>
            Continue
          </Button>
          <Button
            variant="forms.buttons.secondary"
            sx={{
              flex: ['1 1 auto', '0 0 auto'],
              letterSpacing: '-.2px',
              lineHeight: '22px',
            }}
            onClick={handleMoreInfo}>
            View detailed instructions
          </Button>
        </Flex>
      </>
    </OnboardingModalTemplate>
  )
}
